import { useState, useEffect, useCallback, useMemo } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import Breadcrumbs from './Breadcrumbs';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { RouteErrorBoundary } from '@/components/Common/ErrorBoundary';
import useAuthStore from '@/stores/authStore';
import { ROLE_SLUG, ROUTE_PATHS } from '@/constants';
import { useAuth } from '@/hooks/useAuth';
import { setToken } from '@/stores/socket';

const ProtectedRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, refreshTokenIfNeeded } = useAuth();

  const token = useAuthStore(state => state.userInfo.token);
  const user = useAuthStore(state => state.userInfo.user);
  const isHydrated = useAuthStore(state => state.isHydrated);

  const roleData = user?.role as unknown;
  const roleSlug =
    (typeof roleData === 'object' && roleData !== null
      ? (roleData as { slug?: string }).slug
      : null) ?? undefined;

  const isRestrictedRole =
    roleSlug === ROLE_SLUG?.MOUNTAIN_MANAGEMENT ||
    roleSlug === ROLE_SLUG?.INSTRUCTOR;

  // UI States
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  // Removed redundant socket logic - centralized in useSocketLifecycle (AppProvider)
  useEffect(() => {
    if (isAuthenticated) {
      setToken(isAuthenticated);
    }
  }, [isAuthenticated]);

  const matchesRestrictedRoute = useCallback(
    (pathname: string, allowedRoute: string) =>
      pathname === allowedRoute || pathname.startsWith(`${allowedRoute}/`),
    []
  );

  const restrictedRoleRoutes = useMemo(() => {
    if (roleSlug === ROLE_SLUG?.MOUNTAIN_MANAGEMENT) {
      return ['/report-manager/transport-pax-report'];
    }
    if (roleSlug === ROLE_SLUG?.INSTRUCTOR) {
      return [ROUTE_PATHS.DAILY_ROSTER, ROUTE_PATHS.TRANSPORT_ROSTER];
    }
    return [];
  }, [roleSlug]);

  const authState = useMemo(
    () => ({
      hasToken: !!token,
      isAuthenticated
    }),
    [token, isAuthenticated]
  );

  const checkAuthentication = useCallback(async () => {
    if (!isHydrated || authChecked) return;
    setIsCheckingAuth(true);

    try {
      if (authState.hasToken) {
        await refreshTokenIfNeeded();
      }
      if (!authState.isAuthenticated) {
        navigate(ROUTE_PATHS.LOGIN, { replace: true });
      }
    } catch {
      navigate(ROUTE_PATHS.LOGIN, { replace: true });
    } finally {
      setIsCheckingAuth(false);
      setAuthChecked(true);
    }
  }, [isHydrated, authState, authChecked, refreshTokenIfNeeded, navigate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isHydrated) useAuthStore.getState().setHydrated(true);
    }, 5000);
    checkAuthentication();
    return () => clearTimeout(timeoutId);
  }, [checkAuthentication, isHydrated]);

  useEffect(() => {
    if (!isRestrictedRole) return;
    const canAccess = restrictedRoleRoutes.some(route =>
      matchesRestrictedRoute(location.pathname, route)
    );
    if (!canAccess) {
      if (roleSlug === ROLE_SLUG?.INSTRUCTOR) {
        navigate(ROUTE_PATHS.DAILY_ROSTER, { replace: true });
      } else if (roleSlug === ROLE_SLUG?.MOUNTAIN_MANAGEMENT) {
        navigate('/report-manager/transport-pax-report', { replace: true });
      } else {
        navigate(ROUTE_PATHS.DASHBOARD, { replace: true });
      }
    }
  }, [
    isRestrictedRole,
    restrictedRoleRoutes,
    matchesRestrictedRoute,
    location.pathname,
    navigate,
    roleSlug
  ]);

  // --- Render Logic ---

  if (!isHydrated || isCheckingAuth) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600' />
      </div>
    );
  }

  if (!authState.isAuthenticated) return null;

  return (
    <div className='bg-gray-50 flex'>
      {/* 1. Sidebar Component */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* 2. Mobile Overlay */}
      {sidebarOpen && (
        <div
          className='fixed inset-0 bg-black/30 z-30 md:hidden'
          aria-hidden='true'
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* 3. Main Content Container */}
      <div className='flex-1 flex flex-col min-h-screen min-w-0'>
        {/* Header - Contains Toggle and UserMenu */}
        <Header onSidebarToggle={() => setSidebarOpen(true)} />

        {/* Scrollable Content Area */}
        <div className='flex-1 overflow-y-auto overflow-x-hidden touch-pan-y'>
          {/* Breadcrumbs */}
          <Breadcrumbs />

          <a
            href='#main-content'
            className='sr-only focus:not-sr-only bg-white text-blue-700 font-bold px-4 py-2 rounded shadow absolute top-2 left-2 z-50'
          >
            Skip to main content
          </a>

          {/* Main Page Content (Outlet) */}
          <main id='main-content'>
            <RouteErrorBoundary>
              <Outlet />
            </RouteErrorBoundary>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProtectedRoute;
